import { ReactNode, useRef } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

interface TiltCardProps {
  children: ReactNode;
  className?: string;
  max?: number;
  glare?: boolean;
}

/**
 * Lightweight 3D tilt-on-hover. Applies a subtle perspective transform
 * tracking the cursor position. Disabled for reduced-motion users and touch.
 */
export const TiltCard = ({ children, className = '', max = 8, glare = true }: TiltCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const rx = (py - 0.5) * -2 * max;
    const ry = (px - 0.5) * 2 * max;
    el.style.setProperty('--rx', `${rx}deg`);
    el.style.setProperty('--ry', `${ry}deg`);
    el.style.setProperty('--gx', `${px * 100}%`);
    el.style.setProperty('--gy', `${py * 100}%`);
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', `0deg`);
    el.style.setProperty('--ry', `0deg`);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{
        transformStyle: 'preserve-3d',
        transform: 'perspective(900px) rotateX(var(--rx,0)) rotateY(var(--ry,0))',
        transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      className={`relative ${className}`}
    >
      {children}
      {glare && !reduced && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 hover:opacity-100 transition-opacity duration-500"
          style={{
            background:
              'radial-gradient(circle at var(--gx,50%) var(--gy,50%), hsl(var(--primary) / 0.12), transparent 55%)',
          }}
        />
      )}
    </div>
  );
};
